/**
 * The four theming axes. They are independent, and every one of them is
 * expressed as a class on the root element — see design-system/README.md.
 */
export const SKINS = ['kids', 'adult'] as const;
export type Skin = (typeof SKINS)[number];

export const SCHEMES = ['night', 'day'] as const;
export type Scheme = (typeof SCHEMES)[number];

/** What the person chose. 'auto' resolves to a Scheme at render time. */
export const SCHEME_CHOICES = ['night', 'day', 'auto'] as const;
export type SchemeChoice = (typeof SCHEME_CHOICES)[number];

export const ACCENTS = ['brass', 'terracotta', 'rose', 'olive', 'lapis', 'emerald'] as const;
export type Accent = (typeof ACCENTS)[number];

export const ACCENT_LABELS: Record<Accent, string> = {
  brass: 'Brass',
  terracotta: 'Terracotta',
  rose: 'Rose',
  olive: 'Olive',
  lapis: 'Lapis',
  emerald: 'Emerald',
};

/** Hue angles, mirroring the p-* classes in tokens.css. */
export const ACCENT_HUES: Record<Accent, number> = {
  brass: 80,
  terracotta: 38,
  rose: 12,
  olive: 118,
  lapis: 258,
  emerald: 168,
};

export const FUN_LEVELS = ['full', 'soft'] as const;
export type FunLevel = (typeof FUN_LEVELS)[number];

/**
 * The age the fun version runs to. Settled deliberately rather than guessed:
 * the game layer is meant to carry a child through the years the habit is
 * most easily lost, which reaches well past the age prayer becomes their own
 * responsibility. Anyone can change it in Settings at any age.
 */
export const KIDS_MAX_AGE = 14;

export function skinForAge(age: number): Skin {
  return age <= KIDS_MAX_AGE ? 'kids' : 'adult';
}

/** The youngest age the stepper offers. */
export const MIN_AGE = 4;

/**
 * The oldest, and it reads as "14+" rather than 15.
 *
 * Past the kids ceiling the exact number decides nothing — it only picks the
 * plain version — so a stepper that had to be tapped into the forties to reach
 * an adult age simply stopped short of one instead, at 17, and anyone older
 * could not say so. One open-ended step past the threshold covers every age
 * above it in a single tap.
 */
export const MAX_AGE = KIDS_MAX_AGE + 1;

/** Ages arrive from storage and from arithmetic; neither is trusted. */
export function clampAge(age: number): number {
  if (!Number.isFinite(age)) return MIN_AGE;
  return Math.min(MAX_AGE, Math.max(MIN_AGE, Math.round(age)));
}

/** Is this the open-ended top step — "14+" rather than an exact age? */
export function isOpenEndedAge(age: number): boolean {
  return age >= MAX_AGE;
}

export function themeClassName(skin: Skin, scheme: Scheme, accent: Accent, fun: FunLevel): string {
  return `app ${skin} ${scheme} p-${accent} fun-${fun}`;
}
